import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const current = LANGUAGES.find((l) => l.code === i18n.language?.split("-")[0]) ?? LANGUAGES[0];

  const linkColor = scrolled
    ? "text-foreground-900 hover:text-primary-600"
    : "text-background-50 hover:text-background-100";

  const logoTextColor = scrolled ? "text-foreground-950" : "text-background-50";
  const logoAccent = scrolled ? "text-primary-600" : "text-accent-400";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200 shadow-sm"
          : "bg-gradient-to-b from-black/45 to-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-table-line text-background-50 text-lg"></i>
            </div>
            <span className={`font-heading font-bold text-lg whitespace-nowrap ${logoTextColor}`}>
              USFans<span className={logoAccent}>Link</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            <Link
              to="/"
              className={`whitespace-nowrap inline-flex items-center gap-1.5 text-sm font-semibold transition-colors ${linkColor}`}
            >
              <i className="ri-home-4-line"></i>
              {t("nav.homeBtn")}
            </Link>
            <a
              href="#categories"
              className={`whitespace-nowrap text-sm font-medium transition-colors ${linkColor}`}
            >
              {t("nav.categories")}
            </a>
            <a
              href="#articles"
              className={`whitespace-nowrap inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${linkColor}`}
            >
              <i className="ri-book-open-line"></i>
              {t("nav.shoppingGuide")}
            </a>
            <a
              href="#faq"
              className={`whitespace-nowrap text-sm font-medium transition-colors ${linkColor}`}
            >
              {t("nav.faq")}
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setLangOpen((v) => !v)}
                className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled
                    ? "bg-background-100 hover:bg-background-200 text-foreground-800"
                    : "bg-background-50/25 backdrop-blur-sm hover:bg-background-50/40 text-background-50"
                }`}
              >
                <span>{current.flag}</span>
                <span className="hidden sm:inline">{current.label}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? "rotate-180" : ""}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-background-50 rounded-lg border border-background-200 py-1 z-50 shadow-sm">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-background-100 transition-colors cursor-pointer ${
                        current.code === lang.code ? "text-primary-600 font-semibold" : "text-foreground-800"
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setMobileOpen((v) => !v)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                scrolled ? "text-foreground-900" : "text-background-50"
              }`}
              aria-label="Menu"
            >
              <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 max-h-[80vh] overflow-y-auto">
          <nav className="px-4 py-4 flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-2.5 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-home-4-line"></i>
              </span>
              {t("nav.homeBtn")}
            </Link>
            <a
              href="#categories"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2.5 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-grid-line"></i>
              </span>
              {t("nav.categories")}
            </a>
            <a
              href="#articles"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2.5 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                <i className="ri-book-open-line"></i>
              </span>
              {t("nav.shoppingGuide")}
            </a>
            <a
              href="#faq"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2.5 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-700">
                <i className="ri-question-line"></i>
              </span>
              {t("nav.faq")}
            </a>
          </nav>

          <div className="px-4 pb-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500 px-1 mb-2">
              {t("categories.title")}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {categories.map((c) => (
                <a
                  key={c.key}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex flex-col items-center gap-1.5 rounded-lg p-2 bg-background-100 border border-background-200/70 hover:border-primary-300 transition-colors"
                >
                  <div className="w-full h-12 rounded-md overflow-hidden">
                    <img
                      src={c.image}
                      alt={t(`cat.${c.key}`)}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <span className="text-[11px] font-medium text-foreground-800 text-center leading-tight whitespace-nowrap">
                    {t(`cat.${c.key}`)}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}